import { DROPDOWN_LIST_PADDING, DROPDOWN_MENU_ICON_SIZE, DROPDOWN_MENU_MIN_WIDTH, DROPDOWN_OPTION_FONT_SIZE_SMALL, DROPDOWN_OPTION_PADDING_X, DROPDOWN_TRIGGER_GAP_PX, getDropdownOptionStyles, getDropdownPaperStyles, getDropdownPickedRowStyles, getDropdownSurfaceActiveBackground, } from './dropdown-surface';
import { getV2OutlinedInputInputStyles, getV2OutlinedInputRootStyles, getV2RichTextFieldBoxStyles, getV2RichTextFieldLabelStyles, getV2ToggleGroupStyles, V2_DEFAULT_FIELD_SIZE, } from './field-box';
import { FORM_CHIP_GROUP_THEME_NAME, FORM_FIELD_LABEL_THEME_NAME, FORM_FIELD_THEME_NAME, FORM_SWITCH_LABEL_THEME_NAME, FORM_TOGGLE_GROUP_THEME_NAME, RICH_TEXT_FIELD_BOX_THEME_NAME, V2_FIELD_LABEL_GAP, V2_FIELD_LABEL_LINE_HEIGHT, } from './form-field-components';
import { v2MenuItemDangerClass, v2MenuItemSizeClass } from './menu-item-modifiers';
import { getFlipAboveTransitionHandlers, opensBelowAnchor, V2_MENU_ANCHOR_ORIGIN, V2_MENU_TRANSFORM_ORIGIN, } from './menu-placement';
import { focusMenuItemUnderPointer, MENU_ITEM_HIGHLIGHT_SELECTOR } from './menu-pointer-focus';
import { getV2SlashCommandMenuStyleOverrides, SLASH_COMMAND_MENU_THEME_NAME, } from './slash-command-menu';
import { getTableHeadStyles, getTableRowDivider, getTableRowStyles, getTableSortStyles, TABLE_CELL_PADDING_X, TABLE_HEAD_HEIGHT_PX, TABLE_ROW_HEIGHT_PX, } from './table-surface';
/**
 * The v2 theme expressed as DELTAS from the v1 theme. `V2ThemeProvider`
 * deepmerges this over the surrounding v1 theme, so anything not listed here is
 * inherited from v1 unchanged.
 */
export const getV2ThemeOptions = () => {
    // Cast through `unknown`: this is a partial DELTA that `V2ThemeProvider`
    // deepmerges over the full v1 theme, so it need not satisfy the full augmented
    // `ThemeOptions` shape on its own.
    return {
        components: {
            MuiLink: {
                defaultProps: { underline: 'hover' },
                styleOverrides: {
                    root: ({ theme }) => ({
                        // Mockup link color = --primary teal (per mode), the same source the
                        // checked switch and focused field use.
                        color: theme.palette.custom.OV.sys.primary,
                        fontWeight: 600,
                    }),
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: { width: 38, height: 22, padding: 0 },
                    switchBase: ({ theme }) => ({
                        padding: 0,
                        // 3px inset all round → 16px thumb travel to the far edge.
                        margin: 3,
                        transitionDuration: '180ms',
                        '&.Mui-checked': {
                            transform: 'translateX(16px)',
                            '& + .MuiSwitch-track': {
                                opacity: 1,
                                backgroundColor: theme.palette.custom.OV.sys.primary,
                            },
                            '& .MuiSwitch-thumb': {
                                backgroundColor: theme.palette.custom.OV.sys.onPrimary,
                            },
                        },
                        '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.4 },
                        '&.Mui-disabled .MuiSwitch-thumb': { opacity: 0.6 },
                    }),
                    thumb: ({ theme }) => ({
                        width: 16,
                        height: 16,
                        boxShadow: 'none',
                        backgroundColor: theme.palette.custom.OV.sys.onSurfaceVariant,
                    }),
                    track: ({ theme }) => ({
                        borderRadius: 11,
                        opacity: 1,
                        backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
                    }),
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        minHeight: 'unset',
                        borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                    }),
                    flexContainer: { gap: 2 },
                    indicator: ({ theme }) => ({
                        height: 2,
                        borderRadius: 2,
                        backgroundColor: theme.palette.custom.OV.sys.secondary,
                    }),
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        textTransform: 'none',
                        minHeight: 'unset',
                        minWidth: 'auto',
                        padding: '8px 12px 12px',
                        fontSize: 13,
                        fontWeight: 600,
                        color: theme.palette.custom.OV.sys.onSurfaceVariant,
                        '&:hover': { color: theme.palette.custom.OV.sys.onBackground },
                        '&.Mui-selected': { color: theme.palette.custom.OV.sys.onBackground },
                    }),
                },
            },
            MuiMenu: {
                defaultProps: {
                    anchorOrigin: V2_MENU_ANCHOR_ORIGIN,
                    transformOrigin: V2_MENU_TRANSFORM_ORIGIN,
                    // A function so the handlers see the anchor. MUI merges `slotProps` one slot
                    // deep, so a menu that passes its own `slotProps` opts out of the flip.
                    slotProps: { transition: getFlipAboveTransitionHandlers },
                },
                styleOverrides: {
                    paper: ({ theme, ownerState }) => ({
                        ...getDropdownPaperStyles(theme),
                        minWidth: DROPDOWN_MENU_MIN_WIDTH,
                        ...(opensBelowAnchor(ownerState) && {
                            marginTop: theme.typography.pxToRem(DROPDOWN_TRIGGER_GAP_PX),
                        }),
                    }),
                    list: ({ theme }) => ({
                        padding: DROPDOWN_LIST_PADDING,
                        // Doubled class outranks MUI's `.MuiMenuItem-root + .MuiDivider-root`
                        // spacing rule, which is injected later at equal specificity. The
                        // horizontal inset matches a row's own padding, so the rule lines up
                        // with the icon column instead of running the full card width.
                        '& .MuiDivider-root.MuiDivider-root': {
                            borderColor: theme.palette.custom.OV.sys.outlineVariant,
                            margin: `0.25rem ${DROPDOWN_OPTION_PADDING_X}`,
                        },
                    }),
                },
            },
            MuiMenuItem: {
                defaultProps: { onMouseMove: focusMenuItemUnderPointer },
                styleOverrides: {
                    root: ({ theme }) => ({
                        ...getDropdownOptionStyles(),
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.78125rem',
                        fontWeight: 500,
                        minHeight: 'unset',
                        color: theme.palette.custom.OV.sys.onBackground,
                        '&.Mui-selected': getDropdownPickedRowStyles(theme),
                        // Same highlight as a SelectMenu row (`select-menu.elements.tsx`): it follows focus,
                        // which the pointer moves too, so it stays put when the pointer leaves the menu.
                        '&:hover, &.Mui-selected:hover': { backgroundColor: 'transparent' },
                        [MENU_ITEM_HIGHLIGHT_SELECTOR]: {
                            backgroundColor: getDropdownSurfaceActiveBackground(theme),
                        },
                        '&.Mui-disabled': { opacity: 0.4 },
                        // Same specificity as MUI's own `.MuiMenuItem-root .MuiListItemIcon-root`
                        // min-width rule, but injected later, so this wins.
                        '& .MuiListItemIcon-root': {
                            minWidth: 'auto',
                            color: theme.palette.custom.OV.sys.onSurfaceVariant,
                        },
                        // `ListItemText` wraps children in a `Typography`, and MUI's dense
                        // variant sizes the wrapper itself off `typography.body2` — both
                        // would otherwise override the row's per-size font.
                        '& .MuiListItemText-root, & .MuiListItemText-root .MuiTypography-root': {
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            lineHeight: 1.45,
                        },
                        [`&.${v2MenuItemSizeClass.small}`]: {
                            gap: '0.4375rem',
                            padding: '0.375rem 0.5rem',
                            fontSize: DROPDOWN_OPTION_FONT_SIZE_SMALL,
                            '& svg': { width: '0.75rem', height: '0.75rem', fontSize: '0.75rem' },
                        },
                        [`&.${v2MenuItemSizeClass.large}`]: {
                            gap: '0.625rem',
                            padding: '0.625rem 0.75rem',
                            fontSize: '0.875rem',
                            '& svg': { width: '1rem', height: '1rem', fontSize: '1rem' },
                        },
                        [`&.${v2MenuItemDangerClass}`]: {
                            color: theme.palette.custom.OV.sys.error,
                        },
                        [`&.${v2MenuItemDangerClass} .MuiListItemIcon-root`]: {
                            color: theme.palette.custom.OV.sys.error,
                        },
                        '& svg': {
                            width: DROPDOWN_MENU_ICON_SIZE,
                            height: DROPDOWN_MENU_ICON_SIZE,
                            fontSize: DROPDOWN_MENU_ICON_SIZE,
                        },
                    }),
                },
            },
            MuiFormControl: {
                defaultProps: { size: V2_DEFAULT_FIELD_SIZE },
            },
            MuiTextField: {
                defaultProps: { variant: 'outlined', size: V2_DEFAULT_FIELD_SIZE },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: ({ theme, ownerState }) => getV2OutlinedInputRootStyles(theme, ownerState),
                    input: ({ theme, ownerState }) => getV2OutlinedInputInputStyles(theme, ownerState),
                },
            },
            [FORM_FIELD_THEME_NAME]: {
                defaultProps: { labelAbove: true },
            },
            [FORM_CHIP_GROUP_THEME_NAME]: {
                defaultProps: { selectedChipLook: 'tonal' },
            },
            [FORM_FIELD_LABEL_THEME_NAME]: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        lineHeight: V2_FIELD_LABEL_LINE_HEIGHT,
                        marginBottom: theme.spacing(V2_FIELD_LABEL_GAP),
                    }),
                },
            },
            [FORM_SWITCH_LABEL_THEME_NAME]: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        // MUI's -11px offsets the v1 switch's 12px padding, which the v2 switch drops.
                        marginLeft: 0,
                        '& .MuiFormControlLabel-label': {
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            lineHeight: '1rem',
                            color: theme.palette.custom.OV.sys.onBackground,
                        },
                    }),
                },
            },
            [RICH_TEXT_FIELD_BOX_THEME_NAME]: {
                styleOverrides: {
                    root: ({ theme, error, disabled, size, }) => getV2RichTextFieldBoxStyles(theme, { error, disabled, size }),
                    label: ({ theme }) => getV2RichTextFieldLabelStyles(theme),
                },
            },
            [SLASH_COMMAND_MENU_THEME_NAME]: {
                styleOverrides: getV2SlashCommandMenuStyleOverrides(),
            },
            [FORM_TOGGLE_GROUP_THEME_NAME]: {
                styleOverrides: {
                    root: ({ theme, size }) => getV2ToggleGroupStyles(theme, { size }),
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        marginLeft: 0,
                        marginTop: 6,
                        fontSize: '11.5px',
                        lineHeight: 1.45,
                        color: theme.palette.custom.OV.sys.onSurfaceVariant,
                    }),
                },
            },
            // A plain MUI table inside v2 takes the screen-table look; MRT tables add the props
            // MRT sets itself (cell padding, head background, row hover) from `getScreenTableOptions`.
            MuiTableRow: {
                styleOverrides: {
                    root: ({ theme }) => {
                        // Row hover and selection are painted by the table (MRT's cell overlay), so the
                        // row keeps only its typography and no second hover colour stacks on top.
                        const { '&:hover': _hover, transition: _transition, ...rowTypography } = getTableRowStyles(theme, 'screen');
                        return {
                            height: TABLE_ROW_HEIGHT_PX.screen,
                            '&:not(.MuiTableRow-head)': rowTypography,
                        };
                    },
                    head: { height: TABLE_HEAD_HEIGHT_PX },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        padding: `0 ${TABLE_CELL_PADDING_X.screen}`,
                        borderBottom: getTableRowDivider(theme, 'screen'),
                    }),
                    head: ({ theme }) => getTableHeadStyles(theme, 'screen'),
                    // The row carries the body typography, so a cell inherits it instead of body2.
                    body: { fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' },
                },
            },
            MuiTableSortLabel: {
                styleOverrides: {
                    root: ({ theme }) => {
                        const sort = getTableSortStyles(theme, 'screen');
                        return {
                            '&:hover, &.Mui-active': { color: sort.labelHighlightColor },
                            '&.Mui-active .MuiTableSortLabel-icon': sort.activeIcon,
                        };
                    },
                    icon: ({ theme }) => getTableSortStyles(theme, 'screen').icon,
                },
            },
            // v2 tooltips read as a small surface card (border + shadow) instead of the
            // default dark chip. Deliberately no width — the tooltip hugs its content;
            // call sites cap width per-tooltip via `slotProps.tooltip.sx`.
            MuiTooltip: {
                styleOverrides: {
                    tooltip: ({ theme }) => ({
                        backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
                        color: theme.palette.custom.OV.sys.onSurface,
                        border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                        borderRadius: 10,
                        padding: '10px 12px',
                        boxShadow: theme.shadows[3],
                        fontSize: 13,
                        lineHeight: 1.45,
                    }),
                    arrow: ({ theme }) => ({
                        color: theme.palette.custom.OV.sys.surfaceContainerHigh,
                        '&::before': {
                            border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                        },
                    }),
                },
            },
        },
    };
};
